/**
 * Sprint 2.7.6 Step 7 — Outreach Draft approval/edit modal + send flow.
 *
 * Uses ApprovalModal as the chrome shell.
 *
 * Status-dependent action footer:
 *   drafted   → Approve | Save changes (if dirty) | Discard
 *   approved  → Send via my email | Edit (reverts to drafted) | Discard
 *   sent/opened/replied → quick status flips
 *   won / discarded → read-only, no footer
 *
 * Send mode:
 *   mailto:    — when encoded URL length < 1900 chars
 *   copy-paste — otherwise (body too long for direct email-client pre-fill)
 *
 * "I already sent it" link: status flip only, no clipboard action.
 */
import { useState, useEffect } from 'react';
import { ApprovalModal } from './ApprovalModal';
import { api } from '../api';
import type { OutreachDraft, OutreachDraftStatus, BacklinkTarget } from '../types';

// ── Constants ──────────────────────────────────────────────────────────────────

const MAX_SUBJECT_CHARS = 80;
const SOFT_WORD_LIMIT = 500;
const MAILTO_MAX_ENCODED_LEN = 1900;

// Quick status transitions available in sent/opened/replied
const POST_SEND_ACTIONS: Partial<
  Record<OutreachDraftStatus, Array<{ toStatus: OutreachDraftStatus; label: string }>>
> = {
  sent: [
    { toStatus: 'opened',  label: 'Mark as Opened' },
    { toStatus: 'replied', label: 'Mark as Replied' },
    { toStatus: 'won',     label: 'Mark as Won' },
  ],
  opened: [
    { toStatus: 'replied', label: 'Mark as Replied' },
    { toStatus: 'won',     label: 'Mark as Won' },
  ],
  replied: [
    { toStatus: 'won',      label: 'Mark as Won' },
    { toStatus: 'discarded', label: 'Discard' },
  ],
};

const BADGE_COLORS: Record<OutreachDraftStatus, string> = {
  drafted:   'bg-gray-100 text-gray-600',
  approved:  'bg-amber-50 text-amber-600',
  sent:      'bg-blue-50 text-blue-600',
  opened:    'bg-purple-50 text-purple-600',
  replied:   'bg-indigo-50 text-indigo-600',
  won:       'bg-green-50 text-green-700',
  discarded: 'bg-gray-50 text-gray-400',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function wordCount(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function buildMailtoUrl(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function canUseMailto(subject: string, body: string): boolean {
  try {
    return buildMailtoUrl(subject, body).length < MAILTO_MAX_ENCODED_LEN;
  } catch {
    return false;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Send confirm modal ─────────────────────────────────────────────────────────

interface SendConfirmModalProps {
  targetDomain: string;
  subject: string;
  body: string;
  sending: boolean;
  onPrimaryAction: () => Promise<void>;
  onAlreadySent: () => Promise<void>;
  onCancel: () => void;
}

function SendConfirmModal({
  targetDomain,
  subject,
  body,
  sending,
  onPrimaryAction,
  onAlreadySent,
  onCancel,
}: SendConfirmModalProps) {
  const useMailto = canUseMailto(subject, body);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="send-modal-title" className="font-semibold text-brand-ink">
          Sending to {targetDomain}
        </h3>

        <p className="text-sm text-gray-500">
          {useMailto
            ? 'Your draft will open in your default email client with the subject and body pre-filled.'
            : 'Your draft is too long to pre-fill directly in an email client. Copy and paste it into your email client instead.'}
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={() => void onPrimaryAction()}
            disabled={sending}
            className="w-full bg-brand-green text-white font-medium px-4 py-2.5 rounded-xl text-sm hover:bg-brand-green-dark transition-colors disabled:opacity-60"
          >
            {sending
              ? '…'
              : useMailto
              ? 'Open in email client and mark as sent'
              : 'Copy to clipboard and mark as sent'}
          </button>
          <button
            onClick={onCancel}
            disabled={sending}
            className="w-full border border-gray-200 text-gray-600 font-medium px-4 py-2.5 rounded-xl text-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={() => void onAlreadySent()}
            disabled={sending}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline-offset-2 hover:underline"
          >
            I already sent it (mark as sent without copying)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Discard inline confirm ─────────────────────────────────────────────────────

interface DiscardConfirmProps {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DiscardConfirm({ busy, onConfirm, onCancel }: DiscardConfirmProps) {
  return (
    <div className="flex items-center gap-3 text-sm" role="group" aria-label="Confirm discard">
      <span className="text-gray-600">Discard this draft?</span>
      <button
        onClick={onConfirm}
        disabled={busy}
        className="text-red-500 font-medium hover:text-red-600 transition-colors disabled:opacity-50"
      >
        Yes, discard
      </button>
      <button
        onClick={onCancel}
        disabled={busy}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        Keep
      </button>
    </div>
  );
}

// ── Metadata sidebar ───────────────────────────────────────────────────────────

interface MetaSidebarProps {
  draft: OutreachDraft;
  target: BacklinkTarget;
}

function MetaSidebar({ draft, target }: MetaSidebarProps) {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Generated',        value: formatDate(draft.draftGeneratedAt) },
    { label: 'Model',            value: draft.model },
    { label: 'Tokens',           value: `${draft.tokensIn ?? '—'} in / ${draft.tokensOut ?? '—'} out` },
    { label: 'Rationale',        value: target.rationale },
    { label: 'Competitor links', value: String(target.competitorHasLinkCount) },
  ];

  return (
    <dl className="space-y-3">
      {rows.map(({ label, value }) => (
        <div key={label}>
          <dt className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{label}</dt>
          <dd className="text-xs text-gray-600 mt-0.5 leading-relaxed">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface OutreachDraftModalProps {
  /** The draft to display. Pass null to unmount gracefully. */
  draft: OutreachDraft | null;
  /** The backlink target associated with this draft. */
  target: BacklinkTarget | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after any API mutation so the parent can keep its list in sync. */
  onDraftUpdated: (draft: OutreachDraft) => void;
  /** Parent's showToast helper — keeps toast rendering in the pipeline root. */
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export function OutreachDraftModal({
  draft: draftProp,
  target,
  isOpen,
  onClose,
  onDraftUpdated,
  onToast,
}: OutreachDraftModalProps) {
  const [localDraft, setLocalDraft]     = useState<OutreachDraft | null>(null);
  const [subject, setSubject]           = useState('');
  const [body, setBody]                 = useState('');
  const [saving, setSaving]             = useState(false);
  const [transitioning, setTransitioning] = useState<OutreachDraftStatus | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [sendModalOpen, setSendModalOpen]   = useState(false);
  const [sendBusy, setSendBusy]             = useState(false);

  // Re-initialize edit state when a DIFFERENT draft is opened.
  // Using draft id as dep key so local edits aren't lost on parent re-renders
  // that pass back the same draft object with updated fields.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (draftProp) {
      setLocalDraft(draftProp);
      setSubject(draftProp.draftSubject);
      setBody(draftProp.draftBody);
      setConfirmDiscard(false);
      setSendModalOpen(false);
    }
  }, [draftProp?.id]);

  if (!isOpen || !localDraft || !target) return null;

  const isDirty      = subject !== localDraft.draftSubject || body !== localDraft.draftBody;
  const wc           = wordCount(body);
  const fieldsEditable = localDraft.status === 'drafted';
  const isTerminal   = localDraft.status === 'won' || localDraft.status === 'discarded';
  const busy         = saving || transitioning !== null || sendBusy;

  // ── Shared helpers ─────────────────────────────────────────────────────────

  function syncDraft(updated: OutreachDraft) {
    setLocalDraft(updated);
    onDraftUpdated(updated);
  }

  async function saveContent(): Promise<OutreachDraft> {
    const updated = await api.outreachDrafts.updateContent(localDraft!.id, {
      ...(subject !== localDraft!.draftSubject ? { draftSubject: subject } : {}),
      ...(body    !== localDraft!.draftBody    ? { draftBody:    body    } : {}),
    });
    syncDraft(updated);
    return updated;
  }

  async function transitionTo(toStatus: OutreachDraftStatus): Promise<OutreachDraft> {
    setTransitioning(toStatus);
    try {
      const updated = await api.outreachDrafts.updateStatus(localDraft!.id, toStatus);
      syncDraft(updated);
      return updated;
    } finally {
      setTransitioning(null);
    }
  }

  // ── Action handlers ────────────────────────────────────────────────────────

  async function handleSaveChanges() {
    if (!isDirty) return;
    setSaving(true);
    try {
      await saveContent();
      onToast('Changes saved.');
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    setSaving(true);
    try {
      if (isDirty) await saveContent();
      await transitionTo('approved');
      onToast('Draft approved!');
      onClose();
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Approve failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDiscard() {
    try {
      await transitionTo('discarded');
      onToast('Draft discarded.');
      onClose();
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Discard failed', 'error');
      setConfirmDiscard(false);
    }
  }

  async function handleEditRevert() {
    try {
      await transitionTo('drafted');
      // Fields are now editable (localDraft.status === 'drafted')
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Revert failed', 'error');
    }
  }

  async function handleQuickTransition(toStatus: OutreachDraftStatus) {
    try {
      await transitionTo(toStatus);
      if (toStatus === 'won') {
        onToast('Marked as won! 🎉');
        onClose();
      } else if (toStatus === 'discarded') {
        onToast('Draft discarded.');
        onClose();
      }
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Status update failed', 'error');
    }
  }

  // ── Send flow ──────────────────────────────────────────────────────────────

  async function handleSendPrimary() {
    setSendBusy(true);
    try {
      const useMail = canUseMailto(subject, body);
      if (useMail) {
        window.location.href = buildMailtoUrl(subject, body);
        onToast('Opening your email client…');
      } else {
        await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
        onToast('Copied! Paste into your email client and send.');
      }
      await transitionTo('sent');
      setSendModalOpen(false);
      onClose();
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Send failed', 'error');
    } finally {
      setSendBusy(false);
    }
  }

  async function handleAlreadySent() {
    setSendBusy(true);
    try {
      await transitionTo('sent');
      onToast('Marked as sent.');
      setSendModalOpen(false);
      onClose();
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Status update failed', 'error');
    } finally {
      setSendBusy(false);
    }
  }

  // ── Footer renderer ────────────────────────────────────────────────────────

  function renderFooterActions() {
    if (isTerminal) return null;

    if (confirmDiscard) {
      return (
        <DiscardConfirm
          busy={busy}
          onConfirm={() => void handleDiscard()}
          onCancel={() => setConfirmDiscard(false)}
        />
      );
    }

    if (localDraft!.status === 'drafted') {
      return (
        <>
          <button
            onClick={() => void handleApprove()}
            disabled={busy}
            className="bg-brand-green text-white font-medium px-5 py-2 rounded-xl text-sm hover:bg-brand-green-dark transition-colors disabled:opacity-60"
          >
            {transitioning === 'approved' ? 'Approving…' : 'Approve'}
          </button>
          <button
            onClick={() => void handleSaveChanges()}
            disabled={!isDirty || busy}
            className="border border-gray-200 text-gray-700 font-medium px-5 py-2 rounded-xl text-sm hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            onClick={() => setConfirmDiscard(true)}
            disabled={busy}
            className="ml-auto text-red-500 text-sm font-medium px-3 py-2 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-40"
          >
            Discard
          </button>
        </>
      );
    }

    if (localDraft!.status === 'approved') {
      return (
        <>
          <button
            onClick={() => setSendModalOpen(true)}
            disabled={busy}
            className="bg-brand-green text-white font-medium px-5 py-2 rounded-xl text-sm hover:bg-brand-green-dark transition-colors disabled:opacity-60"
          >
            Send via my email
          </button>
          <button
            onClick={() => void handleEditRevert()}
            disabled={busy}
            className="border border-gray-200 text-gray-700 font-medium px-5 py-2 rounded-xl text-sm hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            {transitioning === 'drafted' ? 'Reverting…' : 'Edit'}
          </button>
          <button
            onClick={() => setConfirmDiscard(true)}
            disabled={busy}
            className="ml-auto text-red-500 text-sm font-medium px-3 py-2 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-40"
          >
            Discard
          </button>
        </>
      );
    }

    // sent / opened / replied — quick status flips
    const quickActions = POST_SEND_ACTIONS[localDraft!.status] ?? [];
    if (quickActions.length === 0) return null;

    return (
      <>
        {quickActions.map(({ toStatus, label }) => (
          <button
            key={toStatus}
            onClick={() => void handleQuickTransition(toStatus)}
            disabled={transitioning !== null}
            className={`border font-medium px-4 py-2 rounded-xl text-sm transition-colors disabled:opacity-40 ${
              toStatus === 'won'
                ? 'border-green-200 text-green-600 hover:bg-green-50'
                : toStatus === 'discarded'
                ? 'border-red-100 text-red-500 hover:bg-red-50'
                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {transitioning === toStatus ? '…' : label}
          </button>
        ))}
      </>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <ApprovalModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-semibold text-brand-ink truncate">
              {target.targetDomain}
            </span>
            <span
              className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${BADGE_COLORS[localDraft.status]}`}
            >
              {localDraft.status}
            </span>
            {target.competitorAuthority !== null && (
              <span className="shrink-0 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                DA {target.competitorAuthority}
              </span>
            )}
          </div>
        }
        meta={<MetaSidebar draft={localDraft} target={target} />}
        actions={renderFooterActions() ?? undefined}
      >
        {/* ── Subject ───────────────────────────────────────────────────── */}
        <div className="space-y-1 mb-5">
          <div className="flex items-center justify-between">
            <label
              htmlFor="outreach-subject"
              className="text-xs font-medium text-gray-500 uppercase tracking-wide"
            >
              Subject
            </label>
            <span
              className={`text-xs ${
                subject.length >= MAX_SUBJECT_CHARS ? 'text-red-500' : 'text-gray-400'
              }`}
            >
              {subject.length}/{MAX_SUBJECT_CHARS}
            </span>
          </div>
          <input
            id="outreach-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={MAX_SUBJECT_CHARS}
            disabled={!fieldsEditable}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            aria-label="Email subject"
          />
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label
              htmlFor="outreach-body"
              className="text-xs font-medium text-gray-500 uppercase tracking-wide"
            >
              Message
            </label>
            {wc > SOFT_WORD_LIMIT && (
              <span className="text-xs text-amber-500">
                {wc} words — consider trimming for higher reply rates
              </span>
            )}
          </div>
          <textarea
            id="outreach-body"
            rows={12}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={!fieldsEditable}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-brand-ink leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed resize-none transition-colors"
            aria-label="Email body"
          />
        </div>
      </ApprovalModal>

      {/* ── Send confirm modal (z-[60], above the main modal z-50) ─────── */}
      {sendModalOpen && (
        <SendConfirmModal
          targetDomain={target.targetDomain}
          subject={subject}
          body={body}
          sending={sendBusy}
          onPrimaryAction={handleSendPrimary}
          onAlreadySent={handleAlreadySent}
          onCancel={() => setSendModalOpen(false)}
        />
      )}
    </>
  );
}
